import { Delete, Edit, Save } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import type { AnyEntity, EnrichedEntity, Relationship } from '@/lib/types';

import { RelationshipDescriptionFields } from '@/components/relationship-editor-dialog';

export interface ExistingRelationshipCardProps {
  entity: AnyEntity;
  target: EnrichedEntity;
  relationship: Relationship;
  returnView: 'list' | 'graph';
  isEditing: boolean;
  editingForwardDescription: string;
  editingReverseDescription: string;
  onEditingForwardDescriptionChange: (value: string) => void;
  onEditingReverseDescriptionChange: (value: string) => void;
  onStartEditing: () => void;
  onCancelEditing: () => void;
  onSave: () => void;
  onDelete: () => void;
}

export function ExistingRelationshipCard({
  entity,
  target,
  relationship,
  returnView,
  isEditing,
  editingForwardDescription,
  editingReverseDescription,
  onEditingForwardDescriptionChange,
  onEditingReverseDescriptionChange,
  onStartEditing,
  onCancelEditing,
  onSave,
  onDelete,
}: Readonly<ExistingRelationshipCardProps>) {
  const { t } = useTranslation();
  const isEntityA = relationship.entityAId === entity.id;
  const forwardDescription = relationship.forwardDescription.trim();
  const reverseDescription = relationship.reverseDescription?.trim();
  const outgoingDescription = isEntityA
    ? forwardDescription
    : (reverseDescription || t('relationships.related_to'));
  const incomingDescription = isEntityA ? reverseDescription : forwardDescription;
  const isSymmetric = Boolean(
    reverseDescription
    && forwardDescription.localeCompare(reverseDescription, undefined, { sensitivity: 'accent' }) === 0
  );
  const accessibleRelationshipDescription = isSymmetric || !incomingDescription
    ? `${entity.name} ${outgoingDescription} ${target.name}`
    : `${entity.name} ${outgoingDescription} ${target.name}. ${target.name} ${incomingDescription} ${entity.name}`;

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        gridColumn: isEditing ? '1 / -1' : 'auto',
        p: { xs: 1.25, sm: 1.5 },
      }}
    >
      {isEditing ? (
        <>
          <RelationshipDescriptionFields
            sourceName={isEntityA ? (entity.name || '') : (target.name || '')}
            targetName={isEntityA ? (target.name || '') : (entity.name || '')}
            forwardDescription={editingForwardDescription}
            reverseDescription={editingReverseDescription}
            onForwardDescriptionChange={onEditingForwardDescriptionChange}
            onReverseDescriptionChange={onEditingReverseDescriptionChange}
          />
          <Box sx={{ display: 'flex', justifyContent: 'end', gap: 1, mt: 2, flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant="contained"
              onClick={onSave}
              startIcon={<Save sx={{ fontSize: '16px' }} />}
            >
              {t('common.save')}
            </Button>
            <Button size="small" onClick={onCancelEditing}>
              {t('common.cancel')}
            </Button>
            <IconButton
              aria-label={t('relationships.delete_relationship')}
              size="small"
              color="error"
              onClick={onDelete}
            >
              <Delete sx={{ fontSize: '16px' }} />
            </IconButton>
          </Box>
        </>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) 32px',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr)',
                sm: 'minmax(0, 0.85fr) minmax(100px, 1.25fr) minmax(0, 1fr)',
              },
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'center',
                minHeight: { xs: 48, sm: 'auto' },
                minWidth: 0,
                px: { xs: 0.5, sm: 1 },
              }}
            >
              <Typography
                component={Link}
                to={`/relationships/${entity.entityType}/${entity.id}?view=${returnView}`}
                variant="body1"
                fontWeight={700}
                color="primary.main"
                sx={{
                  bgcolor: 'action.selected',
                  borderRadius: 1,
                  maxWidth: '100%',
                  overflowWrap: 'anywhere',
                  px: 1.25,
                  py: 0.625,
                  textAlign: 'center',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {entity.name}
              </Typography>
            </Box>

            <Stack
              aria-label={accessibleRelationshipDescription}
              role="img"
              alignItems="center"
              justifyContent="center"
              spacing={0.125}
              sx={{
                borderLeft: { xs: 0, sm: 1 },
                borderColor: 'divider',
                borderTop: { xs: 1, sm: 0 },
                my: { xs: 0, sm: 1 },
                minWidth: 0,
                px: { xs: 0.75, sm: 1.25 },
                py: { xs: 1, sm: 0 },
                textAlign: 'center',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontFamily: 'monospace',
                  fontStyle: 'italic',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  lineHeight: 1.25,
                  overflowWrap: 'anywhere',
                  textTransform: 'uppercase',
                }}
              >
                {outgoingDescription}
              </Typography>
              {isSymmetric ? (
                <Box
                  component="span"
                  aria-hidden
                  sx={{ color: 'primary.main', fontFamily: 'monospace', fontSize: '1.4rem', lineHeight: 0.75 }}
                >
                  ⟷
                </Box>
              ) : (
                <>
                  <Box
                    component="span"
                    aria-hidden
                    sx={{
                      color: 'primary.main',
                      fontFamily: 'monospace',
                      fontSize: '1.4rem',
                      lineHeight: 0,
                    }}
                  >
                    ⟶
                  </Box>
                  {incomingDescription && (
                    <>
                      <Box
                        component="span"
                        aria-hidden
                        sx={{ color: 'primary.main', fontFamily: 'monospace', fontSize: '1.4rem', lineHeight: 0.3 }}
                      >
                        ⟵
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontFamily: 'monospace',
                          fontStyle: 'italic',
                          fontWeight: 700,
                          letterSpacing: '0.06em',
                          lineHeight: 1.25,
                          overflowWrap: 'anywhere',
                          pt: 0.5,
                          textTransform: 'uppercase',
                        }}
                      >
                        {incomingDescription}
                      </Typography>
                    </>
                  )}
                </>
              )}
            </Stack>

            <Box
              sx={{
                alignItems: 'center',
                borderLeft: { xs: 0, sm: 1 },
                borderColor: 'divider',
                borderTop: { xs: 1, sm: 0 },
                display: 'flex',
                justifyContent: 'center',
                my: { xs: 0, sm: 1 },
                minHeight: { xs: 48, sm: 'auto' },
                minWidth: 0,
                px: { xs: 0.75, sm: 1.25 },
              }}
            >
              <Typography
                component={Link}
                to={`/relationships/${target.entityType}/${target.id}?view=${returnView}`}
                variant="h5"
                fontWeight={600}
                color="text.primary"
                sx={{
                  maxWidth: '100%',
                  overflowWrap: 'anywhere',
                  textAlign: 'center',
                  textDecoration: 'none',
                  '&:hover': { color: 'primary.main', textDecoration: 'underline' },
                }}
              >
                {target.name}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              justifyContent: 'flex-start',
              py: 0.25,
            }}
          >
            <IconButton
              aria-label={t('relationships.edit_relationship')}
              size="small"
              onClick={onStartEditing}
            >
              <Edit sx={{ fontSize: '16px' }} />
            </IconButton>
            <IconButton
              aria-label={t('relationships.delete_relationship')}
              size="small"
              color="error"
              onClick={onDelete}
            >
              <Delete sx={{ fontSize: '16px' }} />
            </IconButton>
          </Box>
        </Box>
      )}
    </Card>
  );
}
